import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { ElxKeycloakService } from './keycloak.service';
import { PermissionService } from './permission.service';
import { KeycloakService } from 'keycloak-angular';

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private readonly router = inject(Router);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly elxKeycloak = inject(ElxKeycloakService);
  private readonly permissionService = inject(PermissionService);
  private readonly keycloakLibrary = inject(KeycloakService);

  private isRedirectingToLogin = false;

  constructor() {
    // Listen for session expiration events from ElxKeycloakService
    this.elxKeycloak.onSessionExpired$.subscribe(() => {
      this.handleSessionExpired();
    });
  }

  /**
   * Handle session expiration: show popup and redirect to login
   */
  public handleSessionExpired(): void {
    if (this.isRedirectingToLogin) {
      return;
    }

    const tokens = this.elxKeycloak.restorePersistedTokens();
    const source = tokens?.authSource;

    this.isRedirectingToLogin = true;

    // Clear tokens immediately to prevent further authorized requests
    this.elxKeycloak.clearPersistedTokens();
    this.permissionService.clearUserSessionCache();

    // Directly redirect to login without showing a popup
    this.redirectToLogin();
  }

  /**
   * Perform the actual redirection to login page
   */
  private redirectToLogin(): void {
    this.router.navigate(['/login'], { replaceUrl: true }).finally(() => {
      // Small delay before resetting flag to avoid catching same-batch errors
      setTimeout(() => {
        this.isRedirectingToLogin = false;
        // Also reset Keycloak service notification flag
        this.elxKeycloak.resetSessionExpiredFlag();
      }, 1000);
    });
  }

  /**
   * Check if the app is currently showing an expiration dialog or redirecting
   */
  public isHandlingExpiration(): boolean {
    return this.isRedirectingToLogin;
  }
}
